import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ApiError, legacyAppUrl } from '@/lib/api'
import {
  Alert,
  DataTable,
  Empty,
  Kpi,
  KpiGrid,
  Muted,
  PageHeader,
  Section,
  StatusBadge,
  Tabs,
} from '@/components/ui'
import { fetchColaboradores } from '@/features/rh/api'
import { fetchDpNotificacoes } from './api'
import { formatDate } from './utils'

type Filtro = 'todas' | 'proxima' | 'janela' | 'atrasada'

function diasDesde(iso?: string | null) {
  if (!iso) return null
  const d = new Date(iso.slice(0, 10) + 'T12:00:00')
  if (Number.isNaN(d.getTime())) return null
  const hoje = new Date()
  hoje.setHours(12, 0, 0, 0)
  return Math.floor((hoje.getTime() - d.getTime()) / 86400000)
}

function marcoTipo(dias: number) {
  if (dias <= 52) return 45
  return 90
}

function classificar(dias: number | null): Filtro | 'ok' {
  if (dias == null || dias < 0) return 'ok'
  if (dias > 95) return 'ok'
  const marco = marcoTipo(dias)
  const diff = dias - marco
  if (diff > 3) return 'atrasada'
  if (Math.abs(diff) <= 3) return 'janela'
  if (diff >= -10) return 'proxima'
  return 'ok'
}

function tone(f: Filtro | 'ok') {
  if (f === 'atrasada') return 'inativo' as const
  if (f === 'janela') return 'pendente' as const
  if (f === 'proxima') return 'pendente' as const
  return 'ativo' as const
}

function labelStatus(f: Filtro | 'ok') {
  if (f === 'atrasada') return 'Atrasada'
  if (f === 'janela') return 'Na janela'
  if (f === 'proxima') return 'Próxima'
  return 'Em experiência'
}

export function ExperienciaPage() {
  const [filtro, setFiltro] = useState<Filtro>('todas')

  const colabs = useQuery({
    queryKey: ['dp', 'experiencia', 'colabs'],
    queryFn: () =>
      fetchColaboradores({
        status: 'ativo',
        page: 1,
        por_pagina: 500,
      }),
  })

  const notifs = useQuery({
    queryKey: ['dp', 'experiencia', 'notifs'],
    queryFn: () => fetchDpNotificacoes({}),
  })

  const rows = useMemo(() => {
    const list = colabs.data?.dados || []
    return list
      .map((c) => {
        const dias = diasDesde(c.data_admissao)
        const status = classificar(dias)
        const marco = dias != null ? marcoTipo(dias) : 45
        return { ...c, dias, status, marco }
      })
      .filter((c) => c.dias != null && c.dias >= 0 && c.dias <= 100)
      .sort((a, b) => (a.dias ?? 0) - (b.dias ?? 0))
  }, [colabs.data])

  const filtrados = useMemo(() => {
    if (filtro === 'todas') return rows
    return rows.filter((r) => r.status === filtro)
  }, [rows, filtro])

  const counts = useMemo(
    () => ({
      todas: rows.length,
      proxima: rows.filter((r) => r.status === 'proxima').length,
      janela: rows.filter((r) => r.status === 'janela').length,
      atrasada: rows.filter((r) => r.status === 'atrasada').length,
    }),
    [rows],
  )

  const notifExp = (notifs.data?.dados || notifs.data?.data || []).filter((n) =>
    /experiencia|experiência/i.test(String(n.tipo || n.titulo || '')),
  )

  return (
    <section className="hi-page">
      <PageHeader
        title="Avaliação de experiência"
        subtitle="Colaboradores próximos dos marcos de 45 e 90 dias — formulário completo no legado."
        actions={
          <a className="btn btn-primary" href={legacyAppUrl('#/dp-avaliacao-exp')}>
            Avaliar no legado
          </a>
        }
      />

      <KpiGrid>
        <Kpi label="Em experiência (≤100d)" value={String(counts.todas)} />
        <Kpi label="Próximas" value={String(counts.proxima)} />
        <Kpi label="Na janela (±3d)" value={String(counts.janela)} />
        <Kpi label="Atrasadas" value={String(counts.atrasada)} />
        <Kpi label="Alertas DP" value={String(notifExp.length)} />
      </KpiGrid>

      <Tabs
        items={[
          { id: 'todas', label: `Todas (${counts.todas})` },
          { id: 'proxima', label: `Próximas (${counts.proxima})` },
          { id: 'janela', label: `Janela (${counts.janela})` },
          { id: 'atrasada', label: `Atrasadas (${counts.atrasada})` },
        ]}
        value={filtro}
        onChange={(id) => setFiltro(id as Filtro)}
        label="Filtro experiência"
      />

      <Section title="Fila de avaliações" flush>
        {colabs.isLoading ? <Muted>Carregando…</Muted> : null}
        {colabs.isError ? (
          <Alert tone="danger">
            {colabs.error instanceof ApiError
              ? colabs.error.message
              : 'Falha ao listar colaboradores.'}
          </Alert>
        ) : null}
        {!colabs.isLoading && filtrados.length === 0 ? (
          <Empty>Nenhum colaborador neste filtro de experiência.</Empty>
        ) : null}
        {filtrados.length > 0 ? (
          <DataTable>
            <thead>
              <tr>
                <th>Colaborador</th>
                <th>Admissão</th>
                <th>Dias</th>
                <th>Marco</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {filtrados.map((c) => (
                <tr key={c.id}>
                  <td>
                    <strong>{c.nome}</strong>
                    <div>
                      <Muted>
                        {c.cargo_nome || 'Sem cargo'}
                        {c.departamento_nome ? ` · ${c.departamento_nome}` : ''}
                      </Muted>
                    </div>
                  </td>
                  <td>{formatDate(c.data_admissao)}</td>
                  <td>{c.dias ?? '—'}</td>
                  <td>{c.marco}d</td>
                  <td>
                    <StatusBadge tone={tone(c.status)}>{labelStatus(c.status)}</StatusBadge>
                  </td>
                  <td>
                    <Link className="btn btn-secondary btn-sm" to={`/app/rh/${c.id}`}>
                      Perfil
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </DataTable>
        ) : null}
      </Section>

      {notifExp.length > 0 ? (
        <Section title="Notificações de experiência" flush>
          <DataTable>
            <thead>
              <tr>
                <th>Alerta</th>
                <th>Colaborador</th>
                <th>Quando</th>
              </tr>
            </thead>
            <tbody>
              {notifExp.slice(0, 20).map((n) => (
                <tr key={n.id}>
                  <td>{n.titulo || n.tipo}</td>
                  <td>{n.colaborador_nome || '—'}</td>
                  <td>{formatDate(n.criado_em)}</td>
                </tr>
              ))}
            </tbody>
          </DataTable>
        </Section>
      ) : null}
    </section>
  )
}

export default ExperienciaPage
